/*
* @Description: 工具包
* @Author: FengQi
* @Email: zhaolong.dzl@alibaba-inc.com
* @Date:   2018-11-06 15:31:04
* @Last Modified by:   FengQi
* @Last Modified time: 2018-11-19 13:00:36
* @Copyright(c) 2018 Taobao.com
*/

import urls from './const';
import JSONP from '@ali/tarzan-jsonp';

/**
 * 检查一个对象是否为空
 * @param  {Object}  obj 需要检查的对象
 * @return {Boolean}     判断结果
 */
function isEmptyJson(obj) {
  if (!obj) return false;

  if (Object.keys(obj).length === 0) {
    return true;
  }

  return false;
}

/**
 * 生成一个唯一随机数
 * @return {String} 一个 24 位长度的随机字符串，[0-9a-zA-Z]{24}
 */
function uuid() {
  const str = Array.apply(null, Array(4)).reduce((a, b) => b = a + Math.random().toString(36).slice(2, 8), '');
  return str.split('').map(char => Math.floor(Math.random() * 2) ? char.toUpperCase() : char).join('');
}

function encode(str) {
  return encodeURIComponent(str);
}

/**
 * 合并参数，不影响源
 * @param  {Object} source 源对象
 * @param  {Object} obj    合并对象
 * @return {Object}        合并后返回的源对象
 */
function parseParam(source, obj) {
  const keys = Object.keys(source);

  keys.forEach(function (key, index) {
    if (obj[key]) {
      source[key] = obj[key];
    }
  });

  return source;
}

/**
 * 提取对象中特定的数个 key，返回一个新对象，类似 lodash.pick
 *
 * @param {Object} obj      - 目标对象
 * @param {Array}  keyList  - 要提取的 key 数组
 *
 * @return {Object}
 */
function pick(obj, keyList = []) {
  if (!keyList.length) return null;

  return keyList.reduce((a, b) => {
    a[b] = obj[b];
    return a;
  }, {});
}

/**
 * 把json对象转换成以&连接的query字符串
 * @param  {Object} obj 一个obj
 * obj[key] 为对象时被或略；为数组时被转化成一个key多个value，如：a=1&a=2
 *
 * @return {String}     一个以&连接的query 字符串，如：a=1&a=2&b=3
 */
function queryToString(obj = {}) {
  const t = [];

  for (let key in obj) {
    const value = obj[key];
    if (obj.hasOwnProperty(key) && typeof value !== 'undefined') {
      if (Array.isArray(value)) {
        value.forEach(item => t.push(key + '=' + encodeURIComponent(item)));
      } else if (Object.prototype.toString.call(value) !== '[object Object]') {
        t.push(key + '=' + encodeURIComponent(value));
      }
    }
  }

  return t.join('&');
}

/**
 * 融合参数拼接URL
 * @param  {string} url    初始URL
 * @param  {Object} params 需要拼接的参数
 * @return {Sting}         拼接完成后的URL
 */
function genUrl(url, params = {}) {
  return url + (~url.indexOf('?') ? '&' : '?') + queryToString(params);
}

/**
 * 通过key和domain获取请求地址的URL部分
 * @param  {String} name   见"./const"中的字段
 * @param  {Object} domain 配置好的harbor、connect的domain地址
 * @return {String}        拼接完成的URL地址
 */
function getURL(name, domain) {
  // 目前只有收发消息接口的应用是 connected
  if (name === 'polling' || name === 'ack' || name === 'sendOffline') {
    return domain['connected'] + urls.path[name];
  }
  return domain['harbor'] + urls.path[name];
}

/**
 * 接口请求API
 * @param  {String} name    见"./const"中的字段
 * @param  {Object} domain  配置好的harbor、connect的domain地址
 * @param  {Object} params  请求额外参数
 * @param  {Object} options 请求配置
 * @return {Promise}        一个promise对象
 */
function request(name, domain = {
  method: "CORS",
  connected: "//connected.daily.taobao.net",
  harbor: "//webim.daily.taobao.net"
}, params = {}, options = {}) {

  let url = getURL(name, domain);
  if (domain.method.toLocaleUpperCase() === 'JSONP') {

    url = genUrl(url, params);
    return Jsonp(url, options).then(resp => {
      return resp;
    }, err => {
      // 除了 reportHttpRequestIssue 之外，其他接口出错则在服务器记录此次错误, 正常业务接口返回的错误（含err.code)则不report，小二端需要
      /*if (name !== 'reportHttpRequestIssue' && !err.code) {
        request('reportHttpRequestIssue', domain, Object.assign({}, params, {
          requestUrl: encode(url),
          responseError: encode(err && err.toString())
        }))
      }*/
      throw err;
    });
  } else {

    options.params = params;
    return Cors(url, options).then(resp => {
      return resp;
    }, err => {
      // 除了 reportHttpRequestIssue 之外，其他接口出错则在服务器记录此次错误, 正常业务接口返回的错误（含err.code)则不report，小二端需要
      /*if (name !== 'reportHttpRequestIssue' && !err.code) {
        request('reportHttpRequestIssue', domain, Object.assign({}, params, {
          requestUrl: encode(url),
          responseError: encode(err && err.toString())
        }))
      }*/
      throw err;
    });
  }
}

/**
 * 封装的Jsonp请求
 * @param {String} url    完整的请求地址
 * @param {Object} config 请求的配置参数
 * {
 *  timeout: 超时时间
 *  param: 请求参数
 *  name: 请求类型？
 * }
 */
function Jsonp(url, config = {}) {
  const opts = {
    timeout: 30 * 1000,
    charset: 'utf-8'
  };

  if (config.timeout) opts.timeout = config.timeout;
  if (config.param) opts.param = config.param;
  if (config.name) opts.name = config.name;

  return new Promise(function (resolve, reject) {

    // 这里的 timeout 改为 30 秒，便于异地多活切路由时，更早收到消息发送失败的错误
    JSONP(url, opts, function (err, response) {

      //非标准格式
      if (config.standard === false) {
        if (err) {
          reject(err);
        } else {
          resolve(response);
        }
      }
      // 标准格式 standard:
      //    callback({status:1,data:{}});
      //    callback({status:0,data: {message:'', code:''}});
      // SOP新 sop submit:
      //    callback({success:true,data:{}});
      //    callback({success:false,data:{errMsg:'', errCode:''}});
      else {
          if (!err && !response) {
            err = new Error('response data is null');
          }

          if (response && (response.status == 0 || response.success === false)) {
            err = new Error(response.message || response.errMsg);
            err.code = response.code || response.errCode;
          }

          if (err) {
            reject(err);
          } else {
            resolve(response.data);
          }
        }
    });
  });
}

/**
 * 封装的Cors请求
 * @param {String} url    完整的请求地址
 * @param {Object} config 请求的配置参数
 * {
 *  timeout: 超时时间
 *  params: 请求参数
 *  method: 请求类型
 * }
 */
function Cors(url, config = {}) {

  const method = config.method ? config.method.toLowerCase() : 'get';
  const timeout = config.timeout || 30 * 1000;
  let params = config.params || '';

  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();

    xhr.timeout = timeout;

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        const err = new Error(`Uploading failed, please retry.`);
        err.status = xhr.status;
        reject(err);
      } else {
        const text = (xhr.responseText || '').trim();
        const res = text ? JSON.parse(text) : undefined;
        resolve(res);
      }
    };

    xhr.onerror = e => {
      reject(e);
    };

    xhr.ontimeout = e => {
      reject(e);
    };

    if (method === 'get') {
      url = genUrl(url, params);
      params = '';
    }

    xhr.open(method, url, true);

    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.withCredentials = true;
    xhr.send(params);
  });
}

export { isEmptyJson, uuid, parseParam, encode, pick, queryToString, getURL, genUrl, Jsonp, Cors, request };


// WEBPACK FOOTER //
// ./node_modules/_@ali_im-sdk-third@0.1.8@@ali/im-sdk-third/lib/util.js